/**
 * MarketStatusBadge Component
 *
 * 미국 주식시장 개장/휴장 상태 실시간 표시
 */

import React, { useState, useEffect } from 'react';
import './MarketStatusBadge.css';
import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiFetch';

interface MarketStatus {
  is_open: boolean;
  current_time_et: string;
  status_text: string;
  next_event: string;
  hours_until_next: number;
}

const MarketStatusBadge: React.FC = () => {
  const [status, setStatus] = useState<MarketStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Market status 조회
  const fetchMarketStatus = async () => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/market/status`);
      if (!response.ok) {
        throw new Error(`API 오류: ${response.status}`);
      }
      const data = await response.json();
      setStatus(data);
      setError(null);
    } catch (err: any) {
      console.error('Market status 조회 실패:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 초기 로드 및 60초마다 자동 업데이트
  useEffect(() => {
    fetchMarketStatus();
    const interval = setInterval(fetchMarketStatus, 60000); // 60초
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="market-status-badge loading">
        <span className="status-icon">...</span>
        <span className="status-text">로딩 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="market-status-badge error">
        <span className="status-icon">!</span>
        <span className="status-text">오류</span>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  return (
    <div
      className={`market-status-badge ${status.is_open ? 'open' : 'closed'}`}
      title={`${status.next_event}\n현재 시간 (ET): ${status.current_time_et}`}
    >
      <span className="status-icon">{status.is_open ? 'OPEN' : 'CLOSED'}</span>
      <div className="status-content">
        <div className="status-text">{status.status_text}</div>
        <div className="status-detail">
          {status.next_event}
          {status.hours_until_next && (
            <span className="hours-remaining"> ({status.hours_until_next}시간 후)</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketStatusBadge;
